import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import type { SupplierInfo } from '@/types';

export function Suppliers() {
  const api = useApi();
  const suppliers = useQuery({ queryKey: ['admin-suppliers'], queryFn: api.adminListSuppliers });

  if (suppliers.isLoading) return <div className="placeholder">Loading suppliers...</div>;
  if (suppliers.error) return <div className="error-banner">Could not load suppliers: {(suppliers.error as Error).message}</div>;

  const rows = suppliers.data ?? [];

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Suppliers</h1>
          <div className="sub">Upstream AI providers and their licence keys</div>
        </div>
      </div>

      <div className="info-banner">
        Licence keys are written straight to Key Vault — they are never shown again here, and the
        gateway picks up a new key within about 60 seconds.
      </div>

      {rows.length === 0 ? (
        <div className="card"><div className="placeholder">No suppliers configured.</div></div>
      ) : (
        <div className="grid-2">
          {rows.map(s => <SupplierCard key={s.supplier} supplier={s} />)}
        </div>
      )}
    </>
  );
}

function SupplierCard({ supplier }: { supplier: SupplierInfo }) {
  const api = useApi();
  const qc = useQueryClient();
  const [key, setKey] = useState('');
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () => api.adminSetSupplierLicenceKey(supplier.supplier, key),
    onSuccess: () => {
      setKey('');
      setSaved(true);
      qc.invalidateQueries({ queryKey: ['admin-suppliers'] });
    }
  });

  return (
    <div className="card">
      <div className="card-header">
        <h3>{supplier.supplier}</h3>
        <span className={`pill ${supplier.enabled ? 'pill-success' : 'pill-disabled'}`}>
          {supplier.enabled ? 'Enabled' : 'Disabled'}
        </span>
      </div>
      <div className="card-body">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          <span className={`pill ${supplier.hasLicenceKey ? 'pill-success' : 'pill-pending'}`}>
            {supplier.hasLicenceKey ? 'Key stored' : 'No key'}
          </span>
          {supplier.baseUrl && (
            <span className="row-sub" style={{ marginTop: 0 }}>{supplier.baseUrl}</span>
          )}
        </div>

        {save.error && <div className="error-banner">Could not save key: {(save.error as Error).message}</div>}
        {saved && !save.error && (
          <div className="info-banner">Key saved to Key Vault. It applies within ~60 seconds.</div>
        )}

        <div className="field">
          <label htmlFor={`key-${supplier.supplier}`}>{supplier.hasLicenceKey ? 'Rotate licence key' : 'Set licence key'}</label>
          <input
            id={`key-${supplier.supplier}`}
            type="password"
            autoComplete="off"
            placeholder="Paste the API key..."
            value={key}
            onChange={e => { setKey(e.target.value); setSaved(false); }}
          />
        </div>
        <button
          className="btn-primary btn-sm"
          onClick={() => save.mutate()}
          disabled={key.trim() === '' || save.isPending}>
          {save.isPending ? 'Saving...' : 'Save key'}
        </button>
      </div>
    </div>
  );
}
